/**
 * RB Innovations Australia, Product Page JS
 * File: assets/rbi-product.js
 *
 * CHANGELOG (this revision)
 * - Variant swap no longer rewrites the Add to Cart button's innerHTML.
 *   Doing that destroyed the #rbi-atc-text span, which left the inline
 *   Liquid stock script writing labels into a detached node. On the
 *   preorder template that is why the button flipped to "Add to Cart"
 *   after the first variant change. Same fix applied to the AJAX
 *   loading / added states (now CSS classes, DOM stays intact).
 * - The hidden variant input is now updated with setAttribute as well
 *   as .value, so the MutationObserver in the Liquid stock script
 *   actually fires. Previously it never did (property changes are not
 *   attribute mutations). A "rbi:variant-change" event is also
 *   dispatched for anything else that wants to listen.
 * - Sold-out option buttons are marked with .is-soldout (grey with a
 *   diagonal strike, see CSS). Recomputed on every selection.
 * - Option combinations that do not exist as a variant disable the
 *   button with an "Unavailable" label instead of silently keeping the
 *   previous variant.
 * - Prices formatted with thousands separators to match Liquid's
 *   money_with_currency ($1,099.00 AUD, not $1099.00 AUD).
 * - Variant image switching now matches by data-media-id / data-image-id
 *   on the slide, then by image filename. The old check
 *   (img.src.includes(featured_image.id)) never matched a CDN URL.
 * - Lightbox next / prev now keeps the main gallery in sync, so closing
 *   the lightbox no longer leaves the counter, thumb and arrows on the
 *   wrong slide.
 * - Gallery swipe ignores mostly-vertical drags so page scrolling on
 *   mobile no longer flicks slides.
 * - Mobile sticky bar only appears once the purchase row has scrolled
 *   ABOVE the viewport, not while it is still below the fold on load.
 * - Carousel step now reads the real flex gap (2px) instead of a
 *   hard-coded 20px, which was drifting cards off-grid on every step.
 * - Option buttons use aria-checked (they are role="radio"), not
 *   aria-pressed.
 * - Add to Cart errors from /cart/add.js (e.g. only 1 left, you have 1
 *   in cart) are shown inline instead of a full-page fallback submit
 *   that dumped the customer on Shopify's plain error page.
 * - After a successful add, Dawn's cart drawer is refreshed and opened
 *   when the theme has one, and the header count is updated.
 * - Sticky bar variant label ( / Gloss Twill ) updates on swap.
 * - Quantity syncs on "input" as well as "change".
 * - Recently viewed falls back to product JSON when the theme did not
 *   set window.RBI.currentProduct.
 */

(function () {
  'use strict';

  const $  = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    initLayoutStability();
    initAccordions();
    initGallery();
    initVariants();
    initQuantity();
    initStickyBar();
    initRecentlyViewed();
    initCarousel();
    initCartForm();
  }

  /* ═══════════════════════════════════════════════════════════
     MONEY
     Matches Liquid money_with_currency for AUD: $1,099.00 AUD
  ═══════════════════════════════════════════════════════════ */
  function fmt(cents, withCurrency = true) {
    const n = (Number(cents) || 0) / 100;
    const s = n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return '$' + s + (withCurrency ? ' AUD' : '');
  }

  /* ═══════════════════════════════════════════════════════════
     LAYOUT STABILITY
     Rule: nothing in the info column changes height on a variant
     swap. Every element that used to appear / disappear now either
     keeps its space (visibility toggled, not display) or swaps with
     something of identical height.

       Afterpay note      -> keeps its line, fades out when not
                             applicable
       Low stock line     -> reserved line under the status (only
                             if any variant can actually trigger it)
       Backorder / preorder notes -> keep their space when hidden
       Notify Me button   -> lives INSIDE the purchase row and
                             swaps with the Add to Cart form (same
                             height), instead of appearing below it

     The inline Liquid stock script still writes style.display on
     the same ids. The stylesheet forces those elements to stay
     display:block and this module mirrors "display:none" into a
     class that hides them without releasing their space.
  ═══════════════════════════════════════════════════════════ */
  function initLayoutStability() {
    const managed = ['#rbi-installment-note', '#rbi-low-stock', '#rbi-backorder-note', '#rbi-preorder-note'];

    // Decide which reserved slots can ever be used on this product.
    let stockMap = {};
    try { stockMap = JSON.parse($('#rbi-stock-info')?.dataset.variantStock || '{}'); } catch {}
    const lowStockPossible = Object.values(stockMap).some(v => v && v.available && v.tracked && v.qty > 0 && v.qty <= 2);

    managed.forEach(sel => {
      const el = $(sel);
      if (!el) return;
      el.classList.add('rbi-reserved');
      const isNote = /note$/.test(el.id) && el.id !== 'rbi-installment-note';
      const never =
        (el.id === 'rbi-low-stock' && !lowStockPossible) ||
        (isNote && el.textContent.trim() === '');
      if (never) { el.classList.add('rbi-never'); return; }   // never used on this product: takes no space
      const sync = () => {
        const hidden = el.style.display === 'none';
        el.classList.toggle('rbi-hidden', hidden);
        if (hidden) el.setAttribute('aria-hidden', 'true'); else el.removeAttribute('aria-hidden');
      };
      sync();
      new MutationObserver(sync).observe(el, { attributes: true, attributeFilter: ['style'] });
    });

    // Notify Me swaps with the Add to Cart form inside the purchase row
    const row  = $('#rbi-purchase-row');
    const form = $('#rbi-atc-form');
    const bis  = $('.rbi-bis-trigger-wrap');
    if (row && form && bis) {
      bis.hidden = false;
      bis.classList.add('rbi-bis-inrow');
      form.insertAdjacentElement('afterend', bis);
      row.classList.add('has-bis');
    }
  }

  /* Show Notify Me in place of the form (sold out) or the form in
     place of Notify Me (available). Both are the same height. */
  function setSoldOutMode(soldOut) {
    const row  = $('#rbi-purchase-row');
    const form = $('#rbi-atc-form');
    const bis  = $('.rbi-bis-trigger-wrap');
    // Sold out: reserved-but-empty lines (Afterpay, low stock) are
    // allowed to collapse so the status sits right under the price.
    // Between in-stock variants nothing is allowed to move.
    $('.rbi-product-page')?.classList.toggle('is-soldout', !!soldOut);
    if (!row || !form || !bis) return;
    row.classList.toggle('is-soldout', !!soldOut);
    form.classList.toggle('rbi-swapped-out', !!soldOut);
    bis.classList.toggle('rbi-swapped-in', !!soldOut);
    form.setAttribute('aria-hidden', soldOut ? 'true' : 'false');
    bis.setAttribute('aria-hidden', soldOut ? 'false' : 'true');
  }

  function setCollapsed(el, collapsed) {
    if (!el) return;
    el.classList.toggle('is-collapsed', !!collapsed);
    el.setAttribute('aria-hidden', collapsed ? 'true' : 'false');
  }

  /* ═══════════════════════════════════════════════════════════
     ACCORDION
  ═══════════════════════════════════════════════════════════ */
  function initAccordions() {
    $$('[data-accordion]').forEach(acc => {
      const trigger = $('.rbi-accordion__trigger', acc);
      const panel   = $('.rbi-accordion__panel', acc);
      if (!trigger || !panel) return;
      trigger.addEventListener('click', () => {
        const isOpen = trigger.classList.contains('is-open');
        $$('[data-accordion]').forEach(other => {
          const t = $('.rbi-accordion__trigger', other);
          const p = $('.rbi-accordion__panel', other);
          if (t && p && other !== acc) {
            t.classList.remove('is-open');
            t.setAttribute('aria-expanded', 'false');
            p.classList.remove('is-open');
            p.setAttribute('hidden', '');
          }
        });
        trigger.classList.toggle('is-open', !isOpen);
        trigger.setAttribute('aria-expanded', String(!isOpen));
        panel.classList.toggle('is-open', !isOpen);
        if (isOpen) panel.setAttribute('hidden', ''); else panel.removeAttribute('hidden');
      });
    });
  }

  /* ═══════════════════════════════════════════════════════════
     GALLERY, slides, thumbnails, swipe, lightbox
  ═══════════════════════════════════════════════════════════ */
  function initGallery() {
    const gallery = $('#rbi-gallery');
    if (!gallery) return;

    const slides   = $$('.rbi-gallery__slide', gallery);
    const thumbs   = $$('.rbi-gallery__thumb', gallery);
    const counter  = $('#rbi-slide-current');
    const prevBtn  = $('#rbi-prev');
    const nextBtn  = $('#rbi-next');
    const mainArea = $('#rbi-gallery-main');

    const lightbox   = $('#rbi-lightbox');
    const lbImg      = $('#rbi-lightbox-img');
    const lbClose    = $('#rbi-lightbox-btn');
    const lbBackdrop = $('#rbi-lightbox-backdrop');
    const lbPrev     = $('#rbi-lb-prev');
    const lbNext     = $('#rbi-lb-next');
    const lbCounter  = $('#rbi-lb-counter');

    // Move lightbox to body so Dawn cannot clip or overlap it
    if (lightbox && lightbox.parentElement !== document.body) {
      document.body.appendChild(lightbox);
    }

    let current = Math.max(0, slides.findIndex(s => s.classList.contains('is-active')));
    const total = slides.length;

    function goTo(idx) {
      if (total === 0) return;
      if (idx < 0) idx = total - 1;
      if (idx >= total) idx = 0;
      slides[current].classList.remove('is-active');
      thumbs[current]?.classList.remove('is-active');
      thumbs[current]?.setAttribute('aria-current', 'false');
      current = idx;
      slides[current].classList.add('is-active');
      if (counter) counter.textContent = current + 1;
      const thumb = thumbs[current];
      if (thumb) {
        thumb.classList.add('is-active');
        thumb.setAttribute('aria-current', 'true');
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
    }

    function lbShow(idx) {
      if (!lbImg) return;
      goTo(idx);                        // keep main gallery in sync
      const slide = slides[current];
      const img   = slide?.querySelector('img');
      if (!img) return;
      lbImg.src = slide.dataset.full || img.currentSrc || img.src;
      lbImg.alt = img.alt || '';
      if (lbCounter) lbCounter.textContent = (current + 1) + ' / ' + total;
    }

    function openLightbox(idx) {
      if (!lightbox) return;
      lbShow(idx);
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      setTimeout(() => lbClose?.focus(), 60);
    }

    function closeLightbox() {
      if (!lightbox) return;
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      mainArea?.focus?.();
    }

    thumbs.forEach((t, i) => t.addEventListener('click', () => goTo(i)));
    prevBtn?.addEventListener('click', e => { e.stopPropagation(); goTo(current - 1); });
    nextBtn?.addEventListener('click', e => { e.stopPropagation(); goTo(current + 1); });

    // Swipe: horizontal only. Ignore mostly-vertical drags (page scroll).
    function bindSwipe(el, onSwipe) {
      if (!el) return;
      let sx = 0, sy = 0;
      el.addEventListener('touchstart', e => {
        sx = e.changedTouches[0].clientX;
        sy = e.changedTouches[0].clientY;
      }, { passive: true });
      el.addEventListener('touchend', e => {
        const dx = e.changedTouches[0].clientX - sx;
        const dy = e.changedTouches[0].clientY - sy;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) onSwipe(dx);
      }, { passive: true });
    }
    bindSwipe(mainArea, dx => goTo(dx < 0 ? current + 1 : current - 1));
    bindSwipe(lightbox, dx => lbShow(dx < 0 ? current + 1 : current - 1));

    mainArea?.addEventListener('click', e => {
      if (window.innerWidth < 750) return;
      if (e.target.closest('#rbi-prev, #rbi-next')) return;
      openLightbox(current);
    });
    // Keyboard users can open the lightbox too
    mainArea?.addEventListener('keydown', e => {
      if ((e.key === 'Enter' || e.key === ' ') && window.innerWidth >= 750) {
        e.preventDefault();
        openLightbox(current);
      }
    });

    lbClose?.addEventListener('click', closeLightbox);
    lbBackdrop?.addEventListener('click', closeLightbox);
    lbPrev?.addEventListener('click', () => lbShow(current - 1));
    lbNext?.addEventListener('click', () => lbShow(current + 1));

    document.addEventListener('keydown', e => {
      if (!lightbox?.classList.contains('is-open')) return;
      if (e.key === 'Escape')     closeLightbox();
      if (e.key === 'ArrowLeft')  lbShow(current - 1);
      if (e.key === 'ArrowRight') lbShow(current + 1);
    });

    window.RBI = window.RBI || {};
    window.RBI.galleryGoTo   = goTo;
    window.RBI.gallerySlides = slides;
  }

  /* ═══════════════════════════════════════════════════════════
     VARIANT SWITCHING
  ═══════════════════════════════════════════════════════════ */
  function initVariants() {
    const productJSON = $('#rbi-product-json');
    if (!productJSON) return;
    let product;
    try { product = JSON.parse(productJSON.textContent); } catch { return; }
    if (!product || !Array.isArray(product.variants)) return;

    window.RBI = window.RBI || {};
    window.RBI.product = product;

    const optionBtns    = $$('.rbi-option-btn');
    const variantInput  = $('#rbi-variant-id');
    const atcBtn        = $('#rbi-atc-btn');
    const atcText       = $('#rbi-atc-text');
    const stickyAtc     = $('#rbi-sticky-atc');
    const stickyVariant = $('#rbi-sticky-variant');
    const priceBlock    = $('#rbi-price-block');
    const skuValue      = $('#rbi-sku-value');
    const stickyPrice   = $('#rbi-sticky-price');

    const optionCount = product.options.length;
    const selected = {};
    product.options.forEach((_, i) => {
      const pos = i + 1;
      const activeBtn = $(`.rbi-option-btn[data-option-position="${pos}"].is-active`);
      const initial = product.variants.find(v => String(v.id) === String(variantInput?.value));
      selected[pos] = activeBtn?.dataset.value
        || (initial && initial['option' + pos])
        || product.variants[0]['option' + pos];
    });

    function variantFor(sel) {
      return product.variants.find(v => {
        for (let i = 1; i <= optionCount; i++) {
          if (v['option' + i] !== sel[i]) return false;
        }
        return true;
      }) || null;
    }

    /* ── Sold-out swatch marking ──
       A button is "sold out" when, with the OTHER options held at their
       current selection, the variant it would produce exists but is not
       available. A button whose combination does not exist at all is
       marked .is-unavailable (same visual, different semantics). */
    function markSoldOut() {
      optionBtns.forEach(btn => {
        const pos = btn.dataset.optionPosition;
        const trial = { ...selected, [pos]: btn.dataset.value };
        const v = variantFor(trial);
        const soldOut = !!v && !v.available;
        const missing = !v;
        btn.classList.toggle('is-soldout', soldOut);
        btn.classList.toggle('is-unavailable', missing);
        if (soldOut || missing) {
          btn.setAttribute('data-stock-state', soldOut ? 'soldout' : 'unavailable');
          const base = btn.getAttribute('aria-label')?.replace(/ \((sold out|unavailable)\)$/i, '') || btn.dataset.value;
          btn.setAttribute('aria-label', base + (soldOut ? ' (sold out)' : ' (unavailable)'));
        } else {
          btn.removeAttribute('data-stock-state');
          const base = btn.getAttribute('aria-label')?.replace(/ \((sold out|unavailable)\)$/i, '');
          if (base) btn.setAttribute('aria-label', base);
        }
      });
    }

    function setAtcLabel(label) {
      /* Never replace innerHTML here. The inline Liquid stock script
         holds a reference to #rbi-atc-text and overrides this label
         with the correct Pre-Order / Made to Order wording. */
      if (atcText) atcText.textContent = label;
      else if (atcBtn) {
        const svg = atcBtn.querySelector('svg');
        atcBtn.textContent = '';
        if (svg) atcBtn.appendChild(svg);
        atcBtn.appendChild(document.createTextNode(label));
      }
    }

    function updatePrice(variant) {
      if (!priceBlock) return;
      const sale = variant.compare_at_price && variant.compare_at_price > variant.price;
      let saleEl    = priceBlock.querySelector('.rbi-price--sale');
      let regEl     = priceBlock.querySelector('.rbi-price:not(.rbi-price--sale):not(.rbi-price--compare)');
      let compareEl = priceBlock.querySelector('.rbi-price--compare');
      let badgeEl   = priceBlock.querySelector('.rbi-badge--sale');

      if (sale) {
        if (!saleEl || !compareEl || !badgeEl) {
          priceBlock.innerHTML =
            `<span class="rbi-price rbi-price--sale" aria-label="Sale price">${fmt(variant.price)}</span>` +
            `<span class="rbi-price rbi-price--compare" aria-label="Original price">${fmt(variant.compare_at_price)}</span>` +
            `<span class="rbi-badge rbi-badge--sale">Save ${fmt(variant.compare_at_price - variant.price, false)}</span>`;
        } else {
          saleEl.textContent    = fmt(variant.price);
          compareEl.textContent = fmt(variant.compare_at_price);
          badgeEl.textContent   = 'Save ' + fmt(variant.compare_at_price - variant.price, false);
        }
      } else {
        if (!regEl || saleEl || compareEl) {
          priceBlock.innerHTML = `<span class="rbi-price">${fmt(variant.price)}</span>`;
        } else {
          regEl.textContent = fmt(variant.price);
        }
      }
    }

    function switchImage(variant) {
      const slides = window.RBI?.gallerySlides;
      if (!slides?.length || !window.RBI?.galleryGoTo) return;
      const mediaId = variant.featured_media?.id ?? variant.featured_image?.id;
      let idx = -1;
      if (mediaId != null) {
        idx = slides.findIndex(s =>
          String(s.dataset.mediaId) === String(mediaId) ||
          String(s.dataset.imageId) === String(mediaId)
        );
      }
      if (idx < 0 && variant.featured_image?.src) {
        const key = fileKey(variant.featured_image.src);
        idx = slides.findIndex(s => {
          const img = s.querySelector('img');
          return img && fileKey(img.currentSrc || img.src) === key;
        });
      }
      if (idx >= 0) window.RBI.galleryGoTo(idx);
    }

    function fileKey(url) {
      try {
        const p = new URL(url, location.origin).pathname;
        return p.split('/').pop().replace(/_(\d+x\d*|\d*x\d+|pico|icon|thumb|small|compact|medium|large|grande|original|master)(?=\.)/i, '').toLowerCase();
      } catch { return ''; }
    }

    function setVariantInput(id) {
      if (!variantInput) return;
      variantInput.value = id;
      variantInput.setAttribute('value', id);   // fires the Liquid MutationObserver
    }

    function updateUI(variant) {
      if (!variant) {
        // Combination does not exist as a variant
        [atcBtn, stickyAtc].forEach(b => { if (b) b.disabled = true; });
        setAtcLabel('Unavailable');
        if (stickyAtc) stickyAtc.textContent = 'Unavailable';
        setSoldOutMode(true);
        markSoldOut();
        return;
      }

      setVariantInput(variant.id);
      updatePrice(variant);
      if (stickyPrice) stickyPrice.textContent = fmt(variant.price);
      if (stickyVariant) stickyVariant.textContent = ' / ' + variant.title;
      if (skuValue) skuValue.textContent = variant.sku || 'N/A';

      [atcBtn, stickyAtc].forEach(b => { if (b) b.disabled = !variant.available; });
      setAtcLabel(variant.available ? 'Add to Cart' : 'Sold Out');
      if (stickyAtc) stickyAtc.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
      if (atcBtn) atcBtn.setAttribute('aria-label', (variant.available ? 'Add ' : 'Sold out: ') + product.title + ' ' + variant.title);

      // Sold out: the form is replaced by Notify Me (same height, no jump)
      setSoldOutMode(!variant.available);

      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      switchImage(variant);
      markSoldOut();

      document.dispatchEvent(new CustomEvent('rbi:variant-change', { detail: { variant, product } }));
    }

    optionBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const pos = btn.dataset.optionPosition;
        selected[pos] = btn.dataset.value;
        optionBtns.forEach(b => {
          if (b.dataset.optionPosition === pos) {
            b.classList.remove('is-active');
            b.setAttribute('aria-checked', 'false');
          }
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-checked', 'true');
        const lbl = $(`#rbi-selected-${pos}`);
        if (lbl) lbl.textContent = btn.dataset.value;
        updateUI(variantFor(selected));
      });
      // Arrow-key movement inside a radiogroup
      btn.addEventListener('keydown', e => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
        const group = optionBtns.filter(b => b.dataset.optionPosition === btn.dataset.optionPosition);
        const i = group.indexOf(btn);
        const next = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? (i + 1) % group.length : (i - 1 + group.length) % group.length;
        e.preventDefault();
        group[next].focus();
        group[next].click();
      });
    });

    // Initial pass: mark sold-out swatches for the server-rendered state
    markSoldOut();
    const initialVariant = variantFor(selected);
    if (initialVariant) setSoldOutMode(!initialVariant.available);
  }

  /* ═══════════════════════════════════════════════════════════
     QUANTITY
  ═══════════════════════════════════════════════════════════ */
  function initQuantity() {
    const input  = $('#rbi-qty');
    const minus  = $('#rbi-qty-minus');
    const plus   = $('#rbi-qty-plus');
    const hidden = $('#rbi-form-qty');
    if (!input) return;
    const clamp = v => Math.max(1, Math.min(99, parseInt(v, 10) || 1));
    const sync  = () => { if (hidden) hidden.value = input.value; };
    minus?.addEventListener('click', () => { input.value = clamp(clamp(input.value) - 1); sync(); });
    plus?.addEventListener('click',  () => { input.value = clamp(clamp(input.value) + 1); sync(); });
    input.addEventListener('input',  sync);
    input.addEventListener('change', () => { input.value = clamp(input.value); sync(); });
  }

  /* ═══════════════════════════════════════════════════════════
     STICKY BAR (mobile)
     Shows only once the purchase row has scrolled above the viewport.
  ═══════════════════════════════════════════════════════════ */
  function initStickyBar() {
    const bar       = $('#rbi-sticky-bar');
    const row       = $('#rbi-purchase-row');
    const atcForm   = $('#rbi-atc-form');
    const stickyAtc = $('#rbi-sticky-atc');
    if (!bar || !row) return;

    const mq = window.matchMedia('(max-width: 749px)');
    let observer = null;

    function enable() {
      if (observer) return;
      bar.setAttribute('aria-hidden', 'false');
      observer = new IntersectionObserver(([e]) => {
        const scrolledPast = !e.isIntersecting && e.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', scrolledPast);
      }, { threshold: 0 });
      observer.observe(row);
    }
    function disable() {
      observer?.disconnect();
      observer = null;
      bar.classList.remove('is-visible');
      bar.setAttribute('aria-hidden', 'true');
    }

    if (mq.matches) enable();
    mq.addEventListener?.('change', e => e.matches ? enable() : disable());

    stickyAtc?.addEventListener('click', () => {
      if (stickyAtc.disabled) return;
      atcForm?.requestSubmit ? atcForm.requestSubmit() : atcForm?.submit();
    });
  }

  /* ═══════════════════════════════════════════════════════════
     RECENTLY VIEWED
  ═══════════════════════════════════════════════════════════ */
  function initRecentlyViewed() {
    let current = window.RBI?.currentProduct;
    if (!current && window.RBI?.product) {
      const p = window.RBI.product;
      const img = p.featured_image || p.images?.[0] || '';
      current = {
        id: p.id,
        title: p.title,
        url: p.url || location.pathname,
        image: typeof img === 'string' ? img : (img?.src || ''),
        price: (p.price_varies ? 'From ' : '') + fmt(p.price_min ?? p.price, false)
      };
    }
    if (!current) return;

    let history = [];
    try { history = JSON.parse(localStorage.getItem('rbi_rv') || '[]'); } catch {}
    const others = history.filter(p => p.id !== current.id);
    try { localStorage.setItem('rbi_rv', JSON.stringify([current, ...others].slice(0, 10))); } catch {}

    const track = $('#rbi-rv-track');
    if (!track) return;

    const toShow = others.slice(0, 6);
    if (toShow.length > 0) {
      $$('[data-fallback]', track).forEach(el => el.remove());
      toShow.forEach(p => {
        const card = document.createElement('div');
        card.className = 'rbi-product-card';
        card.setAttribute('role', 'listitem');
        card.innerHTML = `
          <a href="${eh(p.url)}" class="rbi-product-card__link" aria-label="${eh(p.title)}">
            <div class="rbi-product-card__img-wrap">
              <img src="${eh(p.image)}" alt="${eh(p.title)}" loading="lazy" class="rbi-product-card__img">
            </div>
            <div class="rbi-product-card__info">
              <h3 class="rbi-product-card__title">${eh(p.title)}</h3>
              <span class="rbi-product-card__price">${eh(p.price)}</span>
            </div>
          </a>
          <a href="${eh(p.url)}" class="rbi-btn rbi-btn--card">View Product</a>
        `;
        track.appendChild(card);
      });
    } else {
      const heading = $('#rbi-carousel-heading');
      if (heading) heading.textContent = 'You May Also Like';
    }
  }

  /* ═══════════════════════════════════════════════════════════
     CAROUSEL
  ═══════════════════════════════════════════════════════════ */
  function initCarousel() {
    const track    = $('#rbi-rv-track');
    const viewport = track?.parentElement;
    const prevBtn  = $('#rbi-rv-prev');
    const nextBtn  = $('#rbi-rv-next');
    const dotsWrap = $('#rbi-rv-dots');
    if (!track || !viewport) return;

    let idx = 0;

    function gapPx() {
      const g = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap);
      return Number.isFinite(g) ? g : 0;
    }

    function update() {
      const cards = $$('.rbi-product-card', track);
      if (!cards.length) return;
      const cardW = cards[0].offsetWidth + gapPx();
      const visible = Math.max(1, Math.round(viewport.offsetWidth / cardW));
      const max = Math.max(0, cards.length - visible);
      idx = Math.max(0, Math.min(idx, max));
      track.style.transform = `translateX(-${idx * cardW}px)`;
      if (prevBtn) prevBtn.disabled = idx === 0;
      if (nextBtn) nextBtn.disabled = idx === max;
      if (dotsWrap) {
        dotsWrap.innerHTML = '';
        for (let i = 0; i <= max; i++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'rbi-carousel-dot' + (i === idx ? ' is-active' : '');
          dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
          dot.addEventListener('click', () => { idx = i; update(); });
          dotsWrap.appendChild(dot);
        }
      }
    }

    prevBtn?.addEventListener('click', () => { idx--; update(); });
    nextBtn?.addEventListener('click', () => { idx++; update(); });

    let tx = 0, ty = 0;
    viewport.addEventListener('touchstart', e => { tx = e.changedTouches[0].clientX; ty = e.changedTouches[0].clientY; }, { passive: true });
    viewport.addEventListener('touchend', e => {
      const dx = e.changedTouches[0].clientX - tx;
      const dy = e.changedTouches[0].clientY - ty;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) { dx < 0 ? idx++ : idx--; update(); }
    }, { passive: true });

    setTimeout(update, 250);
    window.addEventListener('resize', debounce(update, 200));
  }

  /* ═══════════════════════════════════════════════════════════
     CART FORM, AJAX
     Sends id + quantity + properties[...] to /cart/add.js.
     Button states are CSS classes so the DOM inside the button
     (icon + #rbi-atc-text) is never destroyed.
  ═══════════════════════════════════════════════════════════ */
  function initCartForm() {
    const form      = $('#rbi-atc-form');
    const atcBtn    = $('#rbi-atc-btn');
    const stickyAtc = $('#rbi-sticky-atc');
    if (!form) return;

    let errorEl = $('#rbi-atc-error');
    if (!errorEl) {
      errorEl = document.createElement('p');
      errorEl.id = 'rbi-atc-error';
      errorEl.className = 'rbi-atc-error';
      errorEl.setAttribute('role', 'alert');
      form.parentElement?.insertAdjacentElement('afterend', errorEl);
    }
    const errWrap = document.createElement('div');
    errWrap.className = 'rbi-collapse rbi-collapse--error is-collapsed no-anim';
    errorEl.parentElement.insertBefore(errWrap, errorEl);
    errWrap.appendChild(errorEl);
    requestAnimationFrame(() => errWrap.classList.remove('no-anim'));
    const showError = msg => { errorEl.textContent = msg; setCollapsed(errWrap, false); };
    const hideError = () => { setCollapsed(errWrap, true); setTimeout(() => { if (errWrap.classList.contains('is-collapsed')) errorEl.textContent = ''; }, 300); };

    let busy = false;

    form.addEventListener('submit', async e => {
      e.preventDefault();
      if (!atcBtn || busy || atcBtn.disabled) return;
      hideError();
      busy = true;
      atcBtn.classList.add('is-loading');
      atcBtn.setAttribute('aria-busy', 'true');
      if (stickyAtc) stickyAtc.disabled = true;

      try {
        const formData = new FormData(form);
        const payload = {
          id: formData.get('id'),
          quantity: parseInt(formData.get('quantity'), 10) || 1,
          properties: {}
        };
        for (const [key, value] of formData.entries()) {
          const m = key.match(/^properties\[(.+)\]$/);
          if (m && value !== '') payload.properties[m[1]] = value;
        }
        if (Object.keys(payload.properties).length === 0) delete payload.properties;

        // Ask Shopify for the Dawn drawer sections in the same round trip
        const drawer = document.querySelector('cart-drawer');
        const sections = ['cart-icon-bubble'];
        if (drawer) sections.unshift('cart-drawer');
        payload.sections = sections.join(',');
        payload.sections_url = window.location.pathname;

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: JSON.stringify(payload),
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status) {
          throw new Error(data.description || data.message || 'Could not add to cart. Please try again.');
        }

        atcBtn.classList.remove('is-loading');
        atcBtn.classList.add('is-added');

        // Refresh header count
        try {
          const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
          document.querySelectorAll('[data-cart-count], .cart-count-bubble span[aria-hidden]').forEach(el => {
            el.textContent = cart.item_count;
          });
          if (data.sections?.['cart-icon-bubble']) {
            const bubble = document.getElementById('cart-icon-bubble');
            if (bubble) bubble.innerHTML = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section')?.innerHTML || bubble.innerHTML;
          }
        } catch {}

        // Open Dawn's drawer if the theme has one
        if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
          try { drawer.renderContents(data); } catch {}
        }

        document.dispatchEvent(new CustomEvent('rbi:added-to-cart', { detail: data }));

        setTimeout(() => {
          atcBtn.classList.remove('is-added');
          atcBtn.removeAttribute('aria-busy');
          if (stickyAtc) stickyAtc.disabled = false;
          busy = false;
        }, 2000);
      } catch (err) {
        atcBtn.classList.remove('is-loading', 'is-added');
        atcBtn.removeAttribute('aria-busy');
        if (stickyAtc) stickyAtc.disabled = false;
        busy = false;
        showError(err?.message || 'Could not add to cart. Please try again.');
      }
    });

    // Hide any stale error when the variant changes
    document.addEventListener('rbi:variant-change', hideError);
  }

  /* ── Helpers ─────────────────────────────────────────────── */
  function eh(s) {
    return String(s ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function debounce(fn, ms) { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; }

})();